import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  TextField, Button, IconButton, Grid, Alert, MenuItem, InputAdornment,
  CircularProgress, Divider,
} from '@mui/material';
import { Close, PersonAdd, Casino, Visibility, VisibilityOff } from '@mui/icons-material';
import { apiFetch } from '../../utils/api';

const C = {
  bg: '#0C0D1D', card: '#0E1831', cardLight: '#121E38',
  border: '#1E2D55', primary: '#2B6EF5', text: '#F9F9F9',
  textMuted: '#878AA2', textSecondary: '#8384A5',
  success: '#0ECC68', error: '#E01B4F', inputBg: '#10182E',
};

// Mirrors the currencies the wallet page can credit/debit.
const CURRENCIES = ['usdt', 'inr', 'btc', 'eth', 'npr', 'pkr', 'bdt'];

interface FormState {
  username: string; email: string; phone: string; country: string;
  password: string; uid: string; initial_balance: string; currency: string;
}

const EMPTY: FormState = {
  username: '', email: '', phone: '', country: '',
  password: '', uid: '', initial_balance: '', currency: 'usdt',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: C.inputBg, borderRadius: 1.5,
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.primary },
    '&.Mui-focused fieldset': { borderColor: C.primary },
  },
  '& .MuiInputBase-input': { color: C.text, fontSize: '0.88rem' },
  '& .MuiInputLabel-root': { color: C.textSecondary, fontSize: '0.85rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: C.primary },
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create so the caller can refresh its list. */
  onCreated: (user: { id: string; name: string }) => void;
}

const CreateDirectUserDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [created, setCreated] = useState<{ id: string; name: string; password: string } | null>(null);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
  };

  // A hand-made ID is only useful if it's easy to read back to the client.
  const rollUid = () => {
    const uid = String(Math.floor(1_000_000_000 + Math.random() * 9_000_000_000));
    setForm(f => ({ ...f, uid }));
  };

  const rollPassword = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const password = Array.from({ length: 10 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
    setForm(f => ({ ...f, password }));
    setShowPassword(true);
  };

  const reset = () => { setForm(EMPTY); setError(null); setCreated(null); setShowPassword(false); };
  const handleClose = () => { if (!saving) { reset(); onClose(); } };

  const submit = async () => {
    setError(null);
    if (form.username.trim().length < 3) return setError('Username must be at least 3 characters');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (form.uid && !/^\d{6,12}$/.test(form.uid.trim())) return setError('UID must be 6-12 digits');

    setSaving(true);
    try {
      const res = await apiFetch<{ status: string; user: { id: string; name: string } }>(
        '/api/admin/direct-users',
        {
          method: 'POST',
          body: JSON.stringify({
            username: form.username.trim(),
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            country: form.country.trim() || null,
            password: form.password,
            uid: form.uid.trim() || undefined,
            initial_balance: Number(form.initial_balance) || 0,
            currency: form.currency,
          }),
        }
      );
      // Show the credentials once — the password is not retrievable later.
      setCreated({ id: res.user.id, name: res.user.name, password: form.password });
      onCreated(res.user);
    } catch (e: any) {
      let msg = e?.message || 'Failed to create client';
      try { msg = JSON.parse(msg).message || msg; } catch { /* plain-text error */ }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 2, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: `${C.primary}22`, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PersonAdd sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1rem' }}>Create Client Manually</Typography>
            <Typography sx={{ color: C.textMuted, fontSize: '0.72rem' }}>
              Direct client — no agent parent, same as a public signup
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: C.textSecondary }}><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {created ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2, bgcolor: `${C.success}18`, color: C.success, border: `1px solid ${C.success}55` }}>
              Client created. Copy these credentials now — the password cannot be shown again.
            </Alert>
            <Box sx={{ bgcolor: C.cardLight, border: `1px solid ${C.border}`, borderRadius: 2, p: 2.5 }}>
              {([['UID', created.id], ['Username', created.name], ['Password', created.password]] as const).map(([label, value]) => (
                <Box key={label} sx={{ mb: 1.5 }}>
                  <Typography sx={{ color: C.textMuted, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
                  <Typography sx={{ color: C.text, fontSize: '1rem', fontWeight: 700, fontFamily: 'monospace' }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, bgcolor: `${C.error}18`, color: C.error, border: `1px solid ${C.error}55` }}>
                {error}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Username *" value={form.username} onChange={set('username')} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Email" value={form.email} onChange={set('email')} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Phone" value={form.phone} onChange={set('phone')} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Country" value={form.country} onChange={set('country')} sx={fieldSx} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Password *" value={form.password} onChange={set('password')}
                  type={showPassword ? 'text' : 'password'} sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(v => !v)} sx={{ color: C.textSecondary }}>
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                        <Button size="small" onClick={rollPassword} sx={{ color: C.primary, textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}>
                          Generate
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}><Divider sx={{ borderColor: C.border }} /></Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small" label="Manual UID (optional)" value={form.uid} onChange={set('uid')}
                  placeholder="Auto-generated if blank" sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={rollUid} sx={{ color: C.primary }} title="Suggest a UID">
                          <Casino fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" label="Opening balance" value={form.initial_balance} onChange={set('initial_balance')} placeholder="0" sx={fieldSx} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" select label="Currency" value={form.currency} onChange={set('currency')} sx={fieldSx}>
                  {CURRENCIES.map(c => (
                    <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{c.toUpperCase()}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>
                  A non-zero opening balance is credited immediately and recorded in the client's wallet history.
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${C.border}`, px: 3, py: 2, gap: 1 }}>
        {created ? (
          <>
            <Button onClick={reset} sx={{ color: C.textSecondary, textTransform: 'none' }}>Create another</Button>
            <Button onClick={handleClose} variant="contained" sx={{ bgcolor: C.primary, textTransform: 'none', '&:hover': { bgcolor: '#4A85F7' } }}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={saving} sx={{ color: C.textSecondary, textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={submit} disabled={saving} variant="contained"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PersonAdd />}
              sx={{ bgcolor: C.primary, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#4A85F7' }, '&.Mui-disabled': { bgcolor: C.cardLight, color: C.textSecondary } }}
            >
              {saving ? 'Creating…' : 'Create Client'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateDirectUserDialog;
